import {
    Ref,
    useRef,
    useState
} from 'react';

import { Box,Stack, TextField } from '@mui/material';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { EditDrawer } from 'common/components/EditDrawer';
import { closeDrawer, selectNameInConflict, setSaveButtonEnabled } from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES } from 'common/components/errorHandler/ErrorHandler';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { postApiKey } from 'common/services/api/ApiKeys/ApiKeys.thunks';
import {
    EditDrawerContentProps, Operations,
} from 'common/types/TypeDefs';
import { handleEditDrawerError } from 'common/utils/errorHandlers';
import { editDrawerTextFieldStyles } from 'common/utils/StyleHelpers';
import { useSnackbar } from 'notistack';

import { openSuccessDialog } from './apiKeysSlice';

function constructCreateContentComponent(
    title: string,
    processing: boolean,
    nameInConflict: boolean,
    handleSaveBtnEnabled: () => void,
    saveButtonFunction: () => void,
    apikeyNameFieldRef: Ref<HTMLInputElement>,
    appSuiteName: string
) {
    return (
        <EditDrawer
            title={title}
            saveButtonFunction={saveButtonFunction}
            processing={processing}
        >
            <Stack spacing={2}>
                <TextField
                    sx={editDrawerTextFieldStyles(nameInConflict)}
                    inputRef={apikeyNameFieldRef}
                    id="filled-basic"
                    size="small"
                    label="API Key Name"
                    placeholder="Type API Key Name"
                    variant="filled"
                    focused
                    autoFocus={true}
                    onChange={handleSaveBtnEnabled}
                    InputProps={{
                        disableUnderline: nameInConflict
                    }}
                />
                {nameInConflict &&
                  <Box component='div'
                      display='inline'
                      sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                    API Key name already in use
                  </Box>
                }
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    id="filled-basic"
                    size="small"
                    label="Application Suite Name"
                    defaultValue={appSuiteName}
                    variant="filled"
                    disabled
                />
            </Stack>
        </EditDrawer>
    );
}

export function ApiKeyCreateContent({
    title
}: EditDrawerContentProps) {

    const apiKeyNameFieldRef = useRef<HTMLInputElement>(undefined);
    const { enqueueSnackbar } = useSnackbar();

    const [processing, setProcessing] = useState<boolean>(false);
    const appSuite = useAppSelector(selectPageInfo);
    const nameInConflict = useAppSelector(selectNameInConflict);

    const dispatch = useAppDispatch();
    const handleSaveBtnEnabled = () => dispatch(setSaveButtonEnabled(!!apiKeyNameFieldRef?.current?.value));

    const apiKeyPostCallback = () => {
        const apikeyOptions = {
            name: apiKeyNameFieldRef.current?.value ?? '',
            suiteId: appSuite?.id ?? '',
        };
        setProcessing(true);
        dispatch(postApiKey(apikeyOptions))
            .unwrap()
            .then((result) => {
                dispatch(setRows(result.rows));
                dispatch(closeDrawer());
                dispatch(openSuccessDialog(result.response.data.value));
                setProcessing(false);
            })
            .catch((error) => {
                handleEditDrawerError(Operations.Create, extractStatusCode(error.message), 'API Key', dispatch, enqueueSnackbar);
                setProcessing(false);
            });
    };

    return constructCreateContentComponent(
        title,
        processing,
        nameInConflict ?? false,
        handleSaveBtnEnabled,
        apiKeyPostCallback,
        apiKeyNameFieldRef as Ref<HTMLInputElement>,
        appSuite?.name ?? '',
    );
}
